import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Alert, CircularProgress, IconButton,
  Paper, Switch, FormControlLabel, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAdminFetch } from '../hooks/useAdminFetch';

interface SiteSetting {
  key: string;
  value: boolean | string | number;
  type: string;
  description: string;
  updated_at: string | null;
  updated_by: string | null;
}

const AdminSiteSettings: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState<Record<string, boolean | string | number>>({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminFetch(`${apiUrl}/api/admin/settings/`);
      setSettings(data.settings);
      setCanEdit(data.can_edit);

      // Store original values to detect changes
      const originals: Record<string, boolean | string | number> = {};
      data.settings.forEach((s: SiteSetting) => {
        originals[s.key] = s.value;
      });
      setOriginalValues(originals);
      setHasChanges(false);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setError(error.message || 'Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, adminFetch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSettingChange = (key: string, newValue: boolean | string | number) => {
    setSettings(prevSettings =>
      prevSettings.map(s =>
        s.key === key ? { ...s, value: newValue } : s
      )
    );

    // Check if any values differ from original
    const changed = settings.some(s => {
      const currentValue = s.key === key ? newValue : s.value;
      return currentValue !== originalValues[s.key];
    });
    setHasChanges(changed);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Build payload with changed settings only
      const payload: Record<string, boolean | string | number> = {};
      settings.forEach(s => {
        if (s.value !== originalValues[s.key]) {
          payload[s.key] = s.value;
        }
      });

      await adminFetch(`${apiUrl}/api/admin/settings/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      setSuccessMessage('Settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 4000);

      // Reload to get updated timestamps
      await loadSettings();
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setError(error.message || 'Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const getSettingLabel = (key: string): string => {
    const labels: Record<string, string> = {
      'account_creation_enabled': 'Account Creation',
    };
    return labels[key] || key;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">Site Settings</Typography>
          </Box>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only superusers can modify site settings. You can view but not edit.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          {settings.map((setting) => (
            <Box key={setting.key} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
              {setting.type === 'boolean' && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Tooltip title={canEdit ? '' : 'Only superusers can change this setting'}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={setting.value as boolean}
                            onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
                            disabled={!canEdit}
                          />
                        }
                        label={
                          <Typography variant="subtitle1" fontWeight="medium">
                            {getSettingLabel(setting.key)}
                          </Typography>
                        }
                      />
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                      {setting.description}
                    </Typography>
                    {setting.updated_at && (
                      <Typography variant="caption" color="text.disabled" sx={{ ml: 6, display: 'block', mt: 0.5 }}>
                        Last updated: {formatDate(setting.updated_at)}
                        {setting.updated_by && ` by ${setting.updated_by}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ))}

          {settings.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No settings available.
            </Typography>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default AdminSiteSettings;
